import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../api/passwordReset';
import { validateEmail } from '../utils/validation';

// The forgot-password request form (bl-777). Reached from the login page's
// "Forgot your password?" link. Submits an email to identity-service's
// `/forgot-password`, then shows a NEUTRAL success state that never reveals
// whether the email maps to an account (anti-enumeration — the server's uniform
// 202 is the security boundary; this UI must not undo it).
//
// MIRRORS LoginPage conventions: reuses the `register__*` CSS classes (visual
// polish is uid-001-11), uses <Link> for nav, carries data-testid on every
// interactive element (conventions/frontend-patterns.md).

type Status = 'idle' | 'submitting' | 'sent' | 'error';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // Pre-submit shape feedback only. The server is authoritative + uniform; a
    // malformed email simply doesn't submit (and never reaches the wire).
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError(null);

    setStatus('submitting');
    const result = await requestPasswordReset(email);
    // `sent` is the UNIFORM success (202 / any non-5xx) — identical for every
    // outcome. Only a network/5xx fault becomes `error`.
    setStatus(result.kind === 'sent' ? 'sent' : 'error');
  }

  // ── Neutral success state (anti-enumeration) ───────────────────────────────
  // Shown for EVERY successful request regardless of whether the email exists.
  if (status === 'sent') {
    return (
      <main className="register" data-testid="forgot-password-page">
        <section className="register__card">
          <h1 className="register__title">Check your email</h1>
          <p
            className="register__subtitle"
            data-testid="forgot-password-sent-message"
          >
            If an account exists for that email, a reset link is on its way. It
            expires after a little while, so check your spam folder if you
            don&apos;t see it soon.
          </p>
          <p className="register__footer">
            <Link to="/login" data-testid="forgot-password-back-to-login">
              Back to sign in
            </Link>
          </p>
        </section>
      </main>
    );
  }

  // ── Request form ────────────────────────────────────────────────────────────
  return (
    <main className="register" data-testid="forgot-password-page">
      <section className="register__card">
        <h1 className="register__title">Reset your password</h1>
        <p className="register__subtitle">
          Enter your email and we&apos;ll send you a reset link.
        </p>

        {status === 'error' && (
          <p
            className="register__banner register__banner--block"
            data-testid="forgot-password-error"
          >
            Something went wrong on our end. Give it another try.
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate data-testid="forgot-password-form">
          <label className="register__field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-password-email"
            />
            {emailError && (
              <span
                className="register__error"
                data-testid="forgot-password-email-error"
              >
                {emailError}
              </span>
            )}
          </label>

          <button
            type="submit"
            className="register__submit"
            disabled={status === 'submitting'}
            data-testid="forgot-password-submit"
          >
            {status === 'submitting' ? 'Sending…' : 'Send reset link'}
          </button>
        </form>

        <p className="register__footer">
          <Link to="/login" data-testid="forgot-password-back-to-login">
            Back to sign in
          </Link>
        </p>
      </section>
    </main>
  );
}
